import React from 'react';
import styled, { keyframes, css } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$size === 'small' ? '4px' : props.$size === 'fullscreen' ? '24px' : '12px'};

  ${props => props.$size === 'fullscreen' && css`
    width: 100%;
    height: 100%;
  `}
`;

const TimeDisplay = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.05em;

  ${props => props.$size === 'small' && css`
    font-size: 24px;
  `}

  ${props => props.$size === 'medium' && css`
    font-size: 48px;
  `}

  ${props => props.$size === 'large' && css`
    font-size: 72px;
  `}

  ${props => props.$size === 'fullscreen' && css`
    font-size: 120px;
    text-shadow: 0 0 40px rgba(255, 255, 255, 0.3);
  `}

  ${props => props.$isPaused && css`
    animation: ${pulse} 1.5s ease-in-out infinite;
    color: #ffcc00;
  `}

  ${props => props.$isUrgent && css`
    color: #ff4444;
  `}
`;

const ProgressContainer = styled.div`
  width: 100%;
  max-width: ${props => {
    switch (props.$size) {
      case 'small': return '80px';
      case 'medium': return '200px';
      case 'large': return '300px';
      case 'fullscreen': return '400px';
      default: return '200px';
    }
  }};
  height: ${props => props.$size === 'small' ? '4px' : props.$size === 'fullscreen' ? '8px' : '6px'};
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: ${props => props.$isUrgent ? '#ff4444' : 'linear-gradient(90deg, #0099ff, #00ff88)'};
  border-radius: 4px;
  transition: width 0.3s ease-out;
  width: ${props => props.$progress}%;
`;

const PausedLabel = styled.div`
  font-size: ${props => props.$size === 'small' ? '10px' : '14px'};
  color: #ffcc00;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
`;

/**
 * Format seconds into MM:SS or HH:MM:SS display
 */
const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined || seconds < 0) {
    return '--:--';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * TimerDisplay - Countdown timer display component
 *
 * @param {number} remaining - Remaining seconds
 * @param {number} total - Total duration in seconds (for progress calculation)
 * @param {'small' | 'medium' | 'large' | 'fullscreen'} size - Display size
 * @param {boolean} showProgress - Whether to show progress bar
 * @param {boolean} isPaused - Whether timer is paused
 * @param {number} urgentThreshold - Seconds below which display turns red (default: 10)
 */
const TimerDisplay = ({
  remaining,
  total,
  size = 'medium',
  showProgress = true,
  isPaused = false,
  urgentThreshold = 10
}) => {
  const progress = total > 0 ? (remaining / total) * 100 : 0;
  const isUrgent = remaining !== null && remaining <= urgentThreshold && remaining > 0;

  return (
    <Container $size={size}>
      <TimeDisplay
        $size={size}
        $isPaused={isPaused}
        $isUrgent={isUrgent}
      >
        {formatTime(remaining)}
      </TimeDisplay>

      {showProgress && total > 0 && (
        <ProgressContainer $size={size}>
          <ProgressBar $progress={progress} $isUrgent={isUrgent} />
        </ProgressContainer>
      )}

      {isPaused && (
        <PausedLabel $size={size}>Paused</PausedLabel>
      )}
    </Container>
  );
};

export default TimerDisplay;
